import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { RD_COMPONENTS, RdComponentType } from '../_utils';
import { Icon, IconColor, IconID, IconSize } from '../icon';
import styles from './text-area.module.scss';

export type TextareaComponentProps = {
  /**
   * Value of the text area
   */
  value: string;

  /**
   * Aria label attribute for the textarea
   */
  ariaLabel?: string;

  /**
   * textarea container class name
   */
  className?: string;

  /**
   * Name attribute of the textarea.
   */
  name?: string;

  /**
   * unique id
   */
  id?: string;

  /**
   * If true, the textarea will be focused during the first mount.
   * @default false
   */
  autoFocus?: boolean;

  /**
   * If true, the textarea will be disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Textarea placeholder value
   */
  placeholder?: string;

  /**
   * If true, the textarea will be required.
   * @default false
   */
  required?: false;

  /**
   * if on gray background the background color of the textarea
   * changes to a lighter shade.
   * @default false
   */
  onGrayBackground?: boolean;

  /**
   * show error state
   * @default false
   */
  hasError?: boolean;

  /**
   * error or helper text
   */
  helperText?: string;

  /**
   * Whether to put the flex grow property on the component container.
   */
  grow?: boolean;

  /**
   * If true, the textarea will be non-editable.
   * @default false
   */
  readOnly?: boolean;

  /**
   * Overrides the className- styles.textarea defined in this component.
   */
  textAreaClassName?: string;
};

type TextareaProps = TextareaComponentProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea: RdComponentType<TextareaProps> = ({
  value,
  ariaLabel,
  id,
  name,
  className = '',
  textAreaClassName,
  onGrayBackground = false,
  hasError = false,
  helperText,
  disabled,
  onChange = _.noop,
  onFocus = _.noop,
  onBlur = _.noop,
  rows = 3,
  grow,
  readOnly,
  ...restProps
}: TextareaProps) => {
  const textareaID = id || (name ? `textarea-${name}` : name);

  const [inputValue, setInputValue] = useState<string>(value);
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event);
    setInputValue(event.target.value);
  };
  const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus(event);
    setHasFocus(true);
  };
  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur(event);
    setHasFocus(false);
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const containerClassNames = cx(className, styles.container, {
    [styles.hasError]: hasError,
    [styles.hasFocus]: hasFocus,
    [styles.onGrayBackground]: onGrayBackground,
    [styles.grow]: grow,
  });

  const textAreaClassNames = cx(textAreaClassName ?? styles.textarea, {
    [styles.hasError]: hasError,
    [styles.readOnly]: readOnly,
  });

  const getHelpTextMarkup = () => {
    if (helperText) {
      const helperTextClassName = cx(styles.helperText, {
        [styles.hasError]: hasError,
        [styles.disabled]: disabled,
      });
      return (
        <span className={helperTextClassName}>
          {hasError && (
            <span className={styles.iconStyle}>
              <Icon
                color={IconColor.TEXT_COLOR}
                size={IconSize.XSMALL}
                id={IconID.EXCLAMATION_POINT_CIRCLE}
              />
            </span>
          )}
          {helperText}
        </span>
      );
    }
    return null;
  };

  return (
    <div data-radiant-v2>
      <div className={containerClassNames} id={id}>
        <textarea
          rows={rows}
          value={inputValue}
          aria-label={ariaLabel}
          onFocus={handleFocus}
          onBlur={handleBlur}
          id={textareaID}
          onChange={handleChange}
          className={textAreaClassNames}
          disabled={disabled}
          readOnly={readOnly}
          {...restProps}
        />
        {getHelpTextMarkup()}
      </div>
    </div>
  );
};

TextArea.RD_COMPONENT = RD_COMPONENTS.TEXTAREA;
